import './TextField.scss'
import {
  useRef,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type ReactNode,
  type Ref
} from 'react'
import { MdOutlineClose, MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md'
import { clx } from '../clx'
import { IconButton } from '../IconButton'

export interface TextFieldProps extends Omit<
  ComponentProps<'input'>,
  'type' | 'size' | 'value' | 'defaultValue' | 'onChange'
> {
  type?: 'text' | 'email' | 'url' | 'password' | 'search'
  value?: string
  defaultValue?: string
  /** Typed value first; the native event is absent when the value comes from the clear button. */
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void
  /** Show a clear button while there is a value (the portal's resettable list filters). */
  isResettable?: boolean
  /** Mask the value with a show/hide toggle (API keys, client secrets). */
  isSecret?: boolean
  /** Decorative leading icon. */
  icon?: ReactNode
  /** `sm` for dense filter bars. */
  size?: 'sm' | 'md'
  /** Accessible name of the clear button. Defaults to "Clear <aria-label or label text>". */
  clearLabel?: string
}

/** Strip Field's decorative required asterisk from a label's text. */
const labelText = (input: HTMLInputElement) =>
  input.labels?.[0]?.textContent.replace(/\s*\*\s*$/, '').trim() ?? ''

function assignRef<T>(ref: Ref<T> | undefined, node: T | null) {
  if (typeof ref === 'function') ref(node)
  else if (ref) ref.current = node
}

/** Single-line text input. Spread Field's control props onto it. */
export function TextField({
  type = 'text',
  value,
  defaultValue,
  onChange,
  isResettable = false,
  isSecret = false,
  icon,
  size = 'md',
  clearLabel,
  className,
  ref,
  ...rest
}: TextFieldProps) {
  const inner = useRef<HTMLInputElement | null>(null)
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? '')
  const [isRevealed, setIsRevealed] = useState(false)
  const [associatedLabel, setAssociatedLabel] = useState('')
  const current = value ?? uncontrolled

  const isInactive = Boolean(rest.disabled) || Boolean(rest.readOnly)
  const hasClear = isResettable && current !== '' && !isInactive
  const name = rest['aria-label'] ?? associatedLabel
  const clearName = clearLabel ?? (name ? `Clear ${name}` : 'Clear')

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (value === undefined) setUncontrolled(event.target.value)
    onChange?.(event.target.value, event)
  }

  const clear = () => {
    const input = inner.current
    if (value === undefined) {
      if (input) input.value = ''
      setUncontrolled('')
    }
    onChange?.('')
    input?.focus()
  }

  return (
    <div
      className={clx(
        'text-field',
        size !== 'md' && `text-field--is-${size}`,
        icon && 'text-field--has-icon',
        hasClear && 'text-field--has-clear',
        isSecret && 'text-field--has-reveal',
        className
      )}
    >
      {icon && (
        <span className="text-field__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <input
        {...(isSecret && { autoComplete: 'off', spellCheck: false })}
        {...rest}
        ref={(node) => {
          inner.current = node
          assignRef(ref, node)
          // Name the clear button after the field's <label> (Field renders it).
          if (node) setAssociatedLabel(labelText(node))
        }}
        type={isSecret ? (isRevealed ? 'text' : 'password') : type}
        className="text-field__input"
        {...(value !== undefined ? { value } : defaultValue !== undefined && { defaultValue })}
        onChange={handleChange}
      />
      {(hasClear || isSecret) && (
        <span className="text-field__actions">
          {hasClear && (
            <IconButton
              label={clearName}
              icon={<MdOutlineClose />}
              size="sm"
              className="text-field__action"
              onClick={clear}
            />
          )}
          {isSecret && (
            <IconButton
              label={isRevealed ? 'Hide value' : 'Show value'}
              icon={isRevealed ? <MdOutlineVisibilityOff /> : <MdOutlineVisibility />}
              size="sm"
              className="text-field__action"
              disabled={Boolean(rest.disabled)}
              onClick={() => {
                setIsRevealed((v) => !v)
              }}
            />
          )}
        </span>
      )}
    </div>
  )
}
